import React from "react";
import { Link } from "react-router-dom";
import { ArrowIcon } from "../../../assets/icons";
import { HeaderUserIcon } from "../../../assets/icons/header_icons";
import UserImage from "../../user_image";
import { APPLICATION_ROUTES } from "../../../utils/routes";
import ThemeSwitcher from "../theme_switcher";

export const LoginLink = ({ children, onClick, ...props }: React.ComponentProps<typeof Link>) => (
	<Link
		className="flex items-center justify-center gap-2 h-[40px] px-4 rounded-md bg-white dark:bg-dark-surface border border-primary text-primary text-[14px] font-medium no-underline transition-[0.2s] hover:bg-[rgba(114,95,237,0.1)] dark:hover:bg-[rgba(114,95,237,0.15)]"
		onClick={onClick}
		{...props}
	>
		{children}
	</Link>
);

interface Props {
	userData: any;
	onEditProfile: (e: React.MouseEvent) => void;
	onLoginClick: () => void;
	t: (key: string) => string;
}

const ProfileCard = ({ userData, onEditProfile, onLoginClick, t }: Props) => {
	const isAuthenticated = !!userData?._id;
	const userFullName =
		[userData?.user_name, userData?.user_second_name].filter(Boolean).join(" ") ||
		userData?.user_email ||
		"";

	if (!isAuthenticated) {
		const loginLink = (
			<LoginLink to={APPLICATION_ROUTES.LOGIN} onClick={onLoginClick} data-testid="login-link">
				<HeaderUserIcon />
				{t("app-header.auth.login")}
			</LoginLink>
		);
		return (
			<div className="flex items-center gap-2 [&>a]:flex-1">
				{loginLink}
				<ThemeSwitcher />
			</div>
		);
	}

	return (
		<div
			className="flex flex-col p-[14px] rounded-[20px] border border-[#e8e7f5] dark:border-white/10 bg-[#f4f3ff] dark:bg-dark-card"
			data-testid="profile-card"
		>
			<div className="flex flex-row items-start gap-[14px]">
				<UserImage
					userData={userData}
					size="88px"
					fontSize="28px"
					color="var(--color-primary)"
					fontWeight={500}
					backgroundClass="bg-white dark:bg-dark-surface"
					isButton={false}
					border={!userData?.user_image && "2px solid var(--color-primary)"}
				/>
				<div className="flex flex-col gap-[2px] flex-1 min-w-0 self-center">
					<div
						className="text-text-primary dark:text-text-dark text-[20px] font-bold leading-[140%] overflow-hidden text-ellipsis whitespace-nowrap"
						data-testid="profile-card-name"
					>
						{userFullName}
					</div>
					{userData?.user_email && (
						<div
							className="text-text-secondary dark:text-text-dark-muted text-[15px] font-normal leading-[140%] overflow-hidden text-ellipsis whitespace-nowrap"
							data-testid="profile-card-email"
						>
							{userData.user_email}
						</div>
					)}
				</div>
				<ThemeSwitcher className="flex-none -mt-1 -mr-1" />
			</div>
			<button
				className="flex items-center justify-center gap-[6px] h-[36px] px-[14px] mt-4 w-full rounded-md bg-white dark:bg-dark-surface shadow-[0_1px_2px_0_rgba(0,0,0,0.05)] text-primary text-[16px] font-medium leading-[140%] cursor-pointer transition-[0.2s] whitespace-nowrap hover:bg-[#f4f3ff] dark:hover:bg-white/5 [&_svg]:rotate-180 [&_svg]:flex-shrink-0"
				onClick={onEditProfile}
				data-testid="edit-profile-button"
			>
				{t("app-header.sidebar.edit-profile-btn")}
				<ArrowIcon fill="var(--color-primary)" />
			</button>
		</div>
	);
};

export default ProfileCard;
